import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { DevBanner } from './components/shared/DevBanner';
import { ScreenRouter } from './components/shared/ScreenRouter';
import { ValidatePage } from './components/validate/ValidatePage';
import { ProtocolPage } from './components/protocol/ProtocolPage';
import { ReportPage } from './components/report/ReportPage';
import { AdminLayout } from './components/admin/AdminLayout';
import { AdminGuard } from './components/admin/AdminGuard';
import { FormularyDashboard } from './components/admin/FormularyDashboard';
import { ProductForm } from './components/admin/products/ProductForm';
import { FormularyTabView } from './components/admin/FormularyTabView';
import { BundleList } from './components/admin/bundles/BundleList';
import { BundleForm } from './components/admin/bundles/BundleForm';
import { SafetyRuleList } from './components/admin/safety/SafetyRuleList';
import { SafetyRuleForm } from './components/admin/safety/SafetyRuleForm';
import { SeedPage } from './components/admin/seed/SeedPage';
import { RegulatoryDashboard } from './components/admin/regulatory/RegulatoryDashboard';
import { DataOpsDashboard } from './components/admin/dataops/DataOpsDashboard';
import { DocumentOpsCenter } from './components/admin/documents/DocumentOpsCenter';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { PlatformHealthDashboard } from './components/admin/ops/PlatformHealthDashboard';
import { McpDashboard } from './components/admin/mcp/McpDashboard';
import { MarketingModule } from './components/marketing/MarketingModule';
import { PrivacySettings } from './components/settings/PrivacySettings';
import { PrivacyPolicy } from './components/legal/PrivacyPolicy';
import { TermsOfService } from './components/legal/TermsOfService';
import { RefundPolicy } from './components/legal/RefundPolicy';
import { ReconsentGate } from './components/shared/ReconsentGate';
import { OrderSuccess } from './components/order/OrderSuccess';
import { OrderLookup } from './components/order/OrderLookup';
import { VendorLayout } from './components/vendor/VendorLayout';
import { VendorDashboard } from './components/vendor/VendorDashboard';
import { VendorOrderList } from './components/vendor/VendorOrderList';
import { VendorOrderDetail } from './components/vendor/VendorOrderDetail';
import { FinancialDashboard } from './components/admin/financial/FinancialDashboard';
import { PatientJourneyDashboard } from './components/admin/patient-journey/PatientJourneyDashboard';
import { NurseTest } from './components/mobile/NurseTest';
import { TwinTest } from './components/mobile/TwinTest';
import { TeachMode } from './components/mobile/TeachMode';
import { TwinFighter } from './components/mobile/TwinFighter';
import { DocModule } from './components/mobile/DocModule';
import { FashionPlate } from './components/mobile/FashionPlate';
import { PatientChat } from './components/mobile/PatientChat';
import { DemoWalkthrough } from './components/mobile/DemoWalkthrough';
import { QualityHub } from './components/quality/QualityHub';
import { PreLaunchHub } from './components/shared/PreLaunchHub';
import { CaptainPane } from './components/mobile/CaptainPane';
import { PromiseKeeperDashboard } from './components/admin/promise-keeper/PromiseKeeperDashboard';
import { PromisesPage } from './components/admin/promises/PromisesPage';
import { FunnelDashboard } from './components/admin/analytics/FunnelDashboard';
import CalibrationDashboard from './pages/admin/CalibrationDashboard';
import { DailyAuditPage } from './components/admin/audit/DailyAuditPage';
import { ModelPerformanceAuditPage } from './components/admin/audit/ModelPerformanceAuditPage';
import { SecurityAuditLogViewer } from './components/admin/audit/SecurityAuditLogViewer';
import { UnitMappingManagement } from './components/admin/unitMapping/UnitMappingManagement';
import { Cap11MonitoringDashboard } from './components/admin/cap11/Cap11MonitoringDashboard';
import { AnalystReviewDashboard } from './components/admin/analystReview/AnalystReviewDashboard';
import { PatientLogin } from './components/patient/PatientLogin';
import { MyResults } from './components/patient/MyResults';
import { ResultsRecovery } from './components/shared/ResultsRecovery';
import { MeetTheDoctor } from './components/pages/MeetTheDoctor';
import { Support } from './components/pages/Support';
import { CatalogPage } from './components/catalog/CatalogPage';
import { ProductDetail } from './components/catalog/ProductDetail';
import { AccessDeniedToast } from './components/shared/AccessDeniedToast';
import { SubscriptionsPage } from './components/account/SubscriptionsPage';
import { LandingPage } from './components/landing/LandingPage';
import { CurrentMedicationsModal } from './components/CurrentMedicationsModal';
import { ComponentShowcase } from './components/shared/ComponentShowcase';
import { LoginPage as PortalLoginPage } from './portal/pages/LoginPage';
import { PortalUnauthorized } from './portal/lib/authGuard';
import { ReviewPage } from './portal/pages/ReviewPage';
import { DashboardPage } from './portal/pages/DashboardPage';

/** Forces a full page reload to break out of the SPA and load static HTML pages */
function ExternalRedirect({ to: _to }: { to: string }) {
  const fullPath = window.location.pathname;
  window.location.replace(fullPath);
  return null;
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '') || '/'}>
        <DevBanner />
        <AccessDeniedToast />
        <Routes>
          <Route path="/" element={<ReconsentGate><LandingPage /></ReconsentGate>} />
          <Route path="/app" element={<ReconsentGate><ScreenRouter /></ReconsentGate>} />
          <Route path="/app/intake/medications" element={<CurrentMedicationsModal />} />
          <Route path="/validate" element={<ValidatePage />} />
          <Route path="/dev-hub" element={<PreLaunchHub />} />
          <Route path="/dev" element={<PreLaunchHub />} />
          <Route path="/dev/showcase" element={<ComponentShowcase />} />
          <Route path="/landing" element={<ReconsentGate><LandingPage /></ReconsentGate>} />
          <Route path="/protocol" element={<ReconsentGate><ProtocolPage /></ReconsentGate>} />
          <Route path="/report" element={<ReconsentGate><ReportPage /></ReconsentGate>} />
          <Route path="/order/success" element={<OrderSuccess />} />
          <Route path="/order/lookup" element={<OrderLookup />} />
          <Route path="/admin" element={<AdminGuard><AdminLayout /></AdminGuard>}>
            <Route index element={<FormularyDashboard />} />
            <Route path="products" element={<FormularyTabView />} />
            <Route path="products/new" element={<ProductForm />} />
            <Route path="products/:id" element={<ProductForm />} />
            <Route path="bundles" element={<BundleList />} />
            <Route path="bundles/new" element={<BundleForm />} />
            <Route path="bundles/:id" element={<BundleForm />} />
            <Route path="safety" element={<SafetyRuleList />} />
            <Route path="safety/new" element={<SafetyRuleForm />} />
            <Route path="safety/:id" element={<SafetyRuleForm />} />
            <Route path="seed" element={<SeedPage />} />
            <Route path="regulatory" element={<RegulatoryDashboard />} />
            <Route path="data-ops" element={<DataOpsDashboard />} />
            <Route path="financial" element={<FinancialDashboard />} />
            <Route path="patient-journey" element={<PatientJourneyDashboard />} />
            <Route path="documents" element={<ErrorBoundary><DocumentOpsCenter /></ErrorBoundary>} />
            <Route path="ops" element={<PlatformHealthDashboard />} />
            <Route path="mcp" element={<McpDashboard />} />
            <Route path="promise-keeper" element={<PromiseKeeperDashboard />} />
            <Route path="promises" element={<PromisesPage />} />
            <Route path="funnel" element={<FunnelDashboard />} />
            <Route path="analytics" element={<FunnelDashboard />} />
            <Route path="calibration" element={<CalibrationDashboard />} />
            <Route path="audit" element={<DailyAuditPage />} />
            <Route path="model-performance" element={<ModelPerformanceAuditPage />} />
            <Route path="unit-mapping" element={<UnitMappingManagement />} />
            <Route path="cap11-monitoring" element={<Cap11MonitoringDashboard />} />
            <Route path="analyst-reviews" element={<AnalystReviewDashboard />} />
            <Route path="security-audit" element={<SecurityAuditLogViewer />} />
          </Route>
          <Route path="/vendor" element={<VendorLayout />}>
            <Route index element={<VendorDashboard />} />
            <Route path="orders" element={<VendorOrderList />} />
            <Route path="orders/:orderId" element={<VendorOrderDetail />} />
          </Route>
          <Route path="/nurse-test" element={<NurseTest />} />
          <Route path="/twin-test" element={<TwinTest />} />
          <Route path="/teach" element={<TeachMode />} />
          <Route path="/twin-fighter" element={<TwinFighter />} />
          <Route path="/doc-module" element={<DocModule />} />
          <Route path="/fashion-plate" element={<FashionPlate />} />
          <Route path="/patient-chat" element={<PatientChat />} />
          <Route path="/demo" element={<DemoWalkthrough />} />
          <Route path="/captain-test" element={<CaptainPane />} />
          <Route path="/quality" element={<QualityHub />} />
          <Route path="/marketing" element={<MarketingModule />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/settings/privacy" element={<PrivacySettings />} />
          <Route path="/login" element={<PatientLogin />} />
          <Route path="/my-results" element={<MyResults />} />
          <Route path="/r/:sessionId" element={<ResultsRecovery />} />
          <Route path="/doctor" element={<MeetTheDoctor />} />
          <Route path="/support" element={<Support />} />
          <Route path="/contact" element={<Navigate to="/support" replace />} />
          <Route path="/catalog" element={<CatalogPage />} />
          <Route path="/catalog/:productId" element={<ProductDetail />} />
          <Route path="/account/subscriptions" element={<SubscriptionsPage />} />
          <Route path="/research/*" element={<ExternalRedirect to="/research" />} />
          <Route path="/blog/*" element={<ExternalRedirect to="/blog" />} />
          {/* UKA-X01: Expert Clinical Review Portal */}
          <Route path="/portal/login" element={<PortalLoginPage />} />
          <Route path="/portal/unauthorized" element={<PortalUnauthorized />} />
          <Route path="/portal/review" element={<ReviewPage />} />
          <Route path="/portal/dashboard" element={<DashboardPage />} />
          <Route path="*" element={<ScreenRouter />} />
        </Routes>
      </BrowserRouter>
    </AppProvider>
  );
}
